import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { Check, X, Filter, Mail, Trash2, Building2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const STATUS = {
  pending: { label: "EN ATTENTE", cls: "badge-warn" },
  active: { label: "ACTIF", cls: "badge-success" },
  rejected: { label: "REJETÉ", cls: "badge-alert" },
};

export default function SuperAdmin() {
  const { user } = useAuth();
  const isSupport = !!user?.is_support;
  const [orgs, setOrgs] = useState([]);
  const [emails, setEmails] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [tab, setTab] = useState("orgs"); // orgs | emails

  const load = async () => {
    const [o, e] = await Promise.all([
      api.get("/super-admin/organizations"),
      api.get("/super-admin/email-outbox"),
    ]);
    setOrgs(o.data); setEmails(e.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return orgs;
    return orgs.filter((o) => o.status === filter);
  }, [orgs, filter]);

  const counts = useMemo(() => {
    const c = { all: orgs.length, pending: 0, active: 0, rejected: 0 };
    for (const o of orgs) c[o.status] = (c[o.status] || 0) + 1;
    return c;
  }, [orgs]);

  const approve = async (org) => {
    if (!window.confirm(`Approuver "${org.name}" ?\n\nLe compte ${org.admin?.email} pourra se connecter immédiatement et un email de confirmation sera envoyé.`)) return;
    try {
      await api.post(`/super-admin/organizations/${org.id}/approve`);
      toast.success(`✅ "${org.name}" approuvée`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reject = async (org) => {
    if (!window.confirm(`Rejeter "${org.name}" ?\n\n⚠️ L'organisation et son compte admin seront SUPPRIMÉS définitivement. Un email d'explication sera envoyé à ${org.admin?.email}.`)) return;
    try {
      await api.post(`/super-admin/organizations/${org.id}/reject`);
      toast.success(`Demande "${org.name}" rejetée`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const hardDelete = async (org) => {
    const confirm1 = window.confirm(`⚠️ DANGER ⚠️\n\nSupprimer DÉFINITIVEMENT "${org.name}" et TOUTES ses données (produits, ventes, lots, etc.) ?`);
    if (!confirm1) return;
    const txt = window.prompt(`Tapez "${org.name}" pour confirmer la suppression :`);
    if (txt !== org.name) return toast.error("Annulé");
    try {
      await api.delete(`/super-admin/organizations/${org.id}`);
      toast.success("Organisation supprimée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Super-administration" subtitle="SaaS · Validation des inscriptions" testId="super-admin-header" />

      <div className="flex items-center gap-1 border-b border-[var(--border-line)]">
        <button data-testid="tab-orgs" onClick={() => setTab("orgs")} className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "orgs" ? "border-[var(--primary,#C2569B)] text-[var(--primary,#C2569B)]" : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-base)]"}`}>
          <Building2 size={14} className="inline mr-1.5"/>Organisations ({counts.all})
        </button>
        <button data-testid="tab-emails" onClick={() => setTab("emails")} className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "emails" ? "border-[var(--primary,#C2569B)] text-[var(--primary,#C2569B)]" : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-base)]"}`}>
          <Mail size={14} className="inline mr-1.5"/>Emails envoyés ({emails.length})
        </button>
      </div>

      {tab === "orgs" && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Filter size={14} className="text-[var(--text-muted)]" />
            {["all", "pending", "active", "rejected"].map((s) => {
              const lbl = s === "all" ? "Toutes" : STATUS[s].label;
              return (
                <button key={s} data-testid={`f-${s}`} type="button" onClick={() => setFilter(s)} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${filter === s ? "bg-[var(--primary,#C2569B)] text-white border-[var(--primary,#C2569B)]" : "bg-white border-[var(--border-line)] text-[var(--text-muted)] hover:border-[var(--primary,#C2569B)]/50"}`}>
                  {lbl} <span className="opacity-70 ml-1">({counts[s] || 0})</span>
                </button>
              );
            })}
          </div>

          <Surface testId="orgs-table">
            {filtered.length === 0 ? <Empty message="Aucune organisation dans cette catégorie" /> : (
              <table className="data-table">
                <thead>
                  <tr><th>Pharmacie</th><th>Admin</th><th>Téléphone</th><th>Inscrite le</th><th className="text-right">Produits</th><th className="text-right">Ventes</th><th>Statut</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {filtered.map((o) => {
                    const st = STATUS[o.status] || STATUS.active;
                    return (
                      <tr key={o.id} data-testid={`org-${o.id}`}>
                        <td>
                          <div className="font-medium">{o.name}</div>
                          <div className="font-data text-xs text-[var(--text-muted)]">{o.id.slice(0, 8)}…</div>
                        </td>
                        <td>
                          <div>{o.admin?.name || "—"}</div>
                          <div className="text-xs text-[var(--text-muted)]">{o.admin?.email || "—"}</div>
                        </td>
                        <td className="font-data text-[var(--text-muted)]">{o.admin?.phone || "—"}</td>
                        <td className="font-data text-[var(--text-muted)] text-xs">{(o.created_at || "").slice(0, 10)}</td>
                        <td className="font-data text-right">{o.product_count}</td>
                        <td className="font-data text-right">{o.sales_count}</td>
                        <td><span className={st.cls}>{st.label}</span></td>
                        <td>
                          <div className="flex items-center gap-1.5">
                            {o.status === "pending" && (
                              <>
                                <button data-testid={`approve-${o.id}`} onClick={() => approve(o)} className="btn-primary text-xs px-2.5 py-1.5 flex items-center gap-1">
                                  <Check size={12}/> Approuver
                                </button>
                                <button data-testid={`reject-${o.id}`} onClick={() => reject(o)} className="text-[var(--text-muted)] hover:text-[var(--alert,#c32a2a)] text-xs px-2 py-1.5 flex items-center gap-1 border border-[var(--border-line)] rounded">
                                  <X size={12}/> Rejeter
                                </button>
                              </>
                            )}
                            {o.status === "active" && (
                              o.protected && !isSupport ? (
                                <span
                                  className="text-[var(--text-muted)] inline-flex items-center gap-1 text-xs"
                                  title="Organisation protégée — suppression réservée au support technique"
                                  data-testid={`locked-${o.id}`}
                                >
                                  <Lock size={12}/> Protégée
                                </span>
                              ) : (
                                <button data-testid={`del-${o.id}`} onClick={() => hardDelete(o)} className="text-[var(--text-muted)] hover:text-[var(--alert,#c32a2a)]" title="Supprimer définitivement">
                                  <Trash2 size={14}/>
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Surface>
        </>
      )}

      {tab === "emails" && (
        <Surface testId="emails-table">
          <div className="p-4 bg-[var(--primary-soft,#F8E8F1)] border-b border-[var(--primary,#C2569B)]/30 text-sm text-[var(--text-muted)]">
            ℹ️ Les emails sont actuellement en mode <b>queue locale</b> (pas réellement envoyés). Pour activer l'envoi réel, ajoutez votre clé Resend dans <span className="font-data">backend/.env</span> (variable <span className="font-data">RESEND_API_KEY</span>).
          </div>
          {emails.length === 0 ? <Empty message="Aucun email" /> : (
            <table className="data-table">
              <thead>
                <tr><th>Date</th><th>Destinataire</th><th>Sujet</th><th>Statut</th></tr>
              </thead>
              <tbody>
                {emails.map((e) => (
                  <tr key={e.id}>
                    <td className="font-data text-xs text-[var(--text-muted)]">{(e.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="font-data">{e.to}</td>
                    <td className="text-sm">{e.subject}</td>
                    <td><span className={e.status === "sent" ? "badge-success" : "badge-warn"}>{e.status === "sent" ? "ENVOYÉ" : "MOCK (file)"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Surface>
      )}
    </div>
  );
}
